import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FaixaKpis, PageHeader, PilulaStatus, SecaoCard, SegmentedControl } from "@/components/padroes";
import type { Serie } from "@/components/dashboard/GraficoColunas";
import { GraficoEvolucao } from "@/components/dashboard/GraficoEvolucao";
import { CardOndeEstaOCusto, type Quebra } from "@/components/dashboard/CardOndeEstaOCusto";
import { nomeCompetencia, textoVariacao } from "@/components/dashboard/variacao";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { api } from "@/lib/api";
import { formatCents, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";
import { mesCurto, porcento, reaisCompacto, type Composicao, type DashboardRH } from "@/data/dashboard";
import { toast } from "sonner";
import {
  AlertTriangle,
  BarChart3,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Info,
  Loader2,
  Table2,
} from "lucide-react";

type ModoGrafico = "composicao" | "pessoa";

/** Tom do número de uma pendência: ação do RH (laranja), erro (vermelho) ou informação (cinza). */
type TomPendencia = "acao" | "erro" | "info";

const COR_TOM: Record<TomPendencia, string> = {
  acao: "text-primary",
  erro: "text-destructive",
  info: "text-muted-foreground",
};

interface Pendencia {
  n: number;
  texto: string;
  onde: string;
  tom: TomPendencia;
  link: string;
}

const custoPorPessoa = (e: Composicao) => (e.headcount ? e.custo / e.headcount : 0);

/** "R$ 1.234.567,89" -> ["1.234.567", "89"], para o número herói. */
const partesValor = (centavos: number) => {
  const [inteiro, centavosTxt = "00"] = formatCents(centavos).replace(/R\$\s?/, "").trim().split(",");
  return [inteiro, centavosTxt] as const;
};

const RHDashboard = () => {
  const nomeBonificacao = useNomeBonificacao();
  const [dados, setDados] = useState<DashboardRH | null>(null);
  const [competencia, setCompetencia] = useState<string>("");
  const [carregando, setCarregando] = useState(true);
  const [modoGrafico, setModoGrafico] = useState<ModoGrafico>("composicao");
  const [mostrarTabela, setMostrarTabela] = useState(false);
  const [quebra, setQuebra] = useState<Quebra>("tomador");
  const [detalharColunas, setDetalharColunas] = useState(false);
  const [tomadoresAbertos, setTomadoresAbertos] = useState<Set<string>>(new Set());
  /**
   * Janela de 12 meses do gráfico. O backend devolve os 12 meses que terminam
   * na competência pedida; para clicar numa coluna sem a janela "andar", ela é
   * mantida enquanto a competência escolhida estiver dentro dela (com os
   * valores atualizados pela última resposta).
   */
  const [evolucao, setEvolucao] = useState<Composicao[]>([]);

  const load = useCallback(async (c: string) => {
    setCarregando(true);
    // Trocar a competência fecha as linhas expandidas de "Onde está o custo".
    setTomadoresAbertos(new Set());
    try {
      const d = await api.get<DashboardRH>(`/dashboard/rh${c ? `?competencia=${c}` : ""}`);
      setDados(d);
      setCompetencia(d.competencia);
      setEvolucao((base) => {
        if (!base.some((e) => e.competencia === d.competencia)) return d.evolucao;
        const novos = new Map(d.evolucao.map((e) => [e.competencia, e]));
        return base.map((e) => novos.get(e.competencia) ?? e);
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar o dashboard");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void load("");
  }, [load]);

  const alternarTomador = (chave: string) =>
    setTomadoresAbertos((a) => {
      const n = new Set(a);
      if (n.has(chave)) n.delete(chave);
      else n.add(chave);
      return n;
    });

  if (!dados) {
    return (
      <RoleGuard role="rh">
        <AppLayout title="Dashboard">
          <div className="flex justify-center py-20">
            {carregando ? <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /> : "Sem dados."}
          </div>
        </AppLayout>
      </RoleGuard>
    );
  }

  const { total, anterior, porVinculo, status, operacional: op } = dados;
  // Mais recente primeiro; ‹ vai para a anterior da lista e › para a seguinte.
  const competencias = [...new Set([competencia, ...dados.competencias])].sort((a, b) => b.localeCompare(a));
  const indice = competencias.indexOf(competencia);
  const compAnterior = competencias[indice + 1];
  const compSeguinte = indice > 0 ? competencias[indice - 1] : undefined;

  const linkFolha = status.folha ? `/rh/folha/${status.folha.id}` : "/rh/folha";
  const pendencias = (
    [
      op.nfsAguardandoRh > 0 && {
        n: op.nfsAguardandoRh,
        texto: op.nfsAguardandoRh === 1 ? "NF aguardando validação do RH" : "NFs aguardando validação do RH",
        onde: "Notas Fiscais",
        tom: "acao",
        link: "/rh/notas",
      },
      op.emissaoSalario?.pendentes && {
        n: op.emissaoSalario.pendentes,
        texto: `de ${op.emissaoSalario.total} PJ ainda não enviaram a NF de salário`,
        onde: "Folha · Emissão",
        tom: "acao",
        link: linkFolha,
      },
      op.emissaoBonificacao?.pendentes && {
        n: op.emissaoBonificacao.pendentes,
        texto: `NF(s) de ${nomeBonificacao.toLowerCase()} pendentes`,
        onde: nomeBonificacao,
        tom: "acao",
        link: status.bonificacao ? `/rh/bonificacao/${status.bonificacao.id}` : "/rh/bonificacao",
      },
      op.holeritesPendentes > 0 && {
        n: op.holeritesPendentes,
        texto: op.holeritesPendentes === 1 ? "holerite CLT com pendência" : "holerites CLT com pendência",
        onde: "Folha · CLT",
        tom: "erro",
        link: linkFolha,
      },
      op.pendenciasImportacao > 0 && {
        n: op.pendenciasImportacao,
        texto: op.pendenciasImportacao === 1 ? "linha de planilha sem colaborador" : "linhas de planilha sem colaborador",
        onde: "Folha · Pendências",
        tom: "erro",
        link: linkFolha,
      },
      op.cadastrosIncompletos > 0 && {
        n: op.cadastrosIncompletos,
        texto: op.cadastrosIncompletos === 1 ? "cadastro incompleto" : "cadastros incompletos",
        onde: "Headcount",
        tom: "info",
        link: "/rh/headcount",
      },
    ] as (Pendencia | false | 0 | undefined)[]
  ).filter(Boolean) as Pendencia[];

  const temDados = total.custo > 0 || total.headcount.total > 0;
  const folhaFechada = status.folha?.status === "fechada";

  // Partes do custo: os mesmos números da coluna do gráfico desta competência.
  const doMes: Composicao = dados.evolucao.find((e) => e.competencia === competencia) ?? {
    competencia,
    folhaPj: porVinculo.pj.salario + porVinculo.pj.complemento + porVinculo.pj.variaveis,
    folhaClt: porVinculo.clt.salario + porVinculo.clt.variaveis + porVinculo.clt.encargos,
    beneficios: total.beneficios,
    bonificacao: total.bonificacao,
    custo: total.custo,
    headcount: total.headcount.total,
  };
  const somaPartes = doMes.folhaPj + doMes.folhaClt + doMes.beneficios + doMes.bonificacao || 1;
  const partes = [
    {
      chave: "pj",
      rotulo: "Folha PJ",
      cor: "var(--viz-1)",
      valor: doMes.folhaPj,
      apoio: `fixo ${reaisCompacto(porVinculo.pj.salario)} · compl. ${reaisCompacto(porVinculo.pj.complemento)}`,
    },
    {
      chave: "clt",
      rotulo: "Folha CLT",
      cor: "var(--viz-2)",
      valor: doMes.folhaClt,
      apoio: `inclui FGTS de ${reaisCompacto(porVinculo.clt.encargos)}`,
    },
    {
      chave: "ben",
      rotulo: "Benefícios",
      cor: "var(--viz-3)",
      valor: doMes.beneficios,
      apoio: total.headcount.total ? `${formatCents(total.beneficios / total.headcount.total)} por pessoa` : "",
    },
    {
      chave: "bon",
      rotulo: nomeBonificacao,
      cor: "var(--viz-4)",
      valor: doMes.bonificacao,
      apoio: status.bonificacao
        ? `parcela ${status.bonificacao.parcela}/${status.bonificacao.numeroParcelas}`
        : "sem parcela no mês",
    },
  ].filter((p) => p.valor > 0 || p.chave !== "bon");
  const [inteiro, centavos] = partesValor(total.custo);

  // Gráfico
  const porPessoa = modoGrafico === "pessoa";
  const series: Serie[] = porPessoa
    ? [{ chave: "porPessoa", rotulo: "Custo total ÷ pessoas na folha", cor: "var(--viz-1)" }]
    : [
        { chave: "folhaPj", rotulo: "Folha PJ", cor: "var(--viz-1)" },
        { chave: "folhaClt", rotulo: "Folha CLT", cor: "var(--viz-2)" },
        { chave: "beneficios", rotulo: "Benefícios", cor: "var(--viz-3)" },
        { chave: "bonificacao", rotulo: nomeBonificacao, cor: "var(--viz-4)" },
      ];
  const valorGrafico = (e: Composicao) => (porPessoa ? custoPorPessoa(e) : e.custo);
  // Média dos meses com custo: meses antes do sistema entrar em uso não puxam a média para baixo.
  const comValor = evolucao.map(valorGrafico).filter((v) => v > 0);
  const media = comValor.length ? comValor.reduce((a, b) => a + b, 0) / comValor.length : null;
  const formatarMedia = porPessoa ? formatCents : reaisCompacto;

  return (
    <RoleGuard role="rh">
      <AppLayout title="Dashboard">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 tabular-nums">
          <PageHeader
            titulo="Visão do mês"
            subtitulo="Custo, pessoas e pendências da empresa na competência selecionada."
            acoes={
              <div className="flex items-center rounded-[10px] border bg-card p-[3px] shadow-[0_2px_12px_-4px_hsl(210_33%_10%/0.08)]">
                <button
                  type="button"
                  title="Competência anterior"
                  aria-label="Competência anterior"
                  disabled={!compAnterior || carregando}
                  onClick={() => compAnterior && void load(compAnterior)}
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-[7px] text-muted-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-[0.35]"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="flex h-[34px] min-w-[196px] items-center justify-center gap-2 rounded-[7px] px-3 text-sm font-semibold transition-colors hover:bg-muted-soft"
                    >
                      <Calendar className="h-4 w-4 text-primary" />
                      {nomeCompetencia(competencia)}
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="max-h-80 min-w-[196px] overflow-auto rounded-[10px] shadow-dropdown">
                    {competencias.map((c) => (
                      <DropdownMenuItem
                        key={c}
                        onSelect={() => c !== competencia && void load(c)}
                        className={cn("text-[13px]", c === competencia && "bg-primary/[0.08] font-semibold")}
                      >
                        {nomeCompetencia(c)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <button
                  type="button"
                  title="Próxima competência"
                  aria-label="Próxima competência"
                  disabled={!compSeguinte || carregando}
                  onClick={() => compSeguinte && void load(compSeguinte)}
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-[7px] text-muted-foreground transition-colors hover:bg-muted disabled:pointer-events-none disabled:opacity-[0.35]"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            }
          />

          {/* Linha de status da competência */}
          <div className="-mt-2 flex flex-wrap items-center gap-2">
            <PilulaStatus cor={status.folha ? (folhaFechada ? "verde" : "ambar") : "cinza"}>
              {status.folha ? (folhaFechada ? "Folha fechada" : "Folha em rascunho") : "Folha não aberta"}
            </PilulaStatus>
            {status.bonificacao && (
              <PilulaStatus cor={status.bonificacao.status === "fechada" ? "verde" : "ambar"}>
                {nomeBonificacao} {status.bonificacao.parcela}/{status.bonificacao.numeroParcelas} ·{" "}
                {status.bonificacao.status === "fechada" ? "fechada" : "rascunho"}
              </PilulaStatus>
            )}
            <PilulaStatus cor="cinza">
              {status.folhasBeneficio} {status.folhasBeneficio === 1 ? "folha" : "folhas"} de benefícios
            </PilulaStatus>
            {status.folha && !folhaFechada && (
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5" />
                Folha em rascunho: os valores podem mudar até o fechamento.
              </span>
            )}
            {carregando && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>

          <div className={cn("flex flex-col gap-5 transition-opacity", carregando && "opacity-60")}>
            {/* Bloco principal: custo (2) + pendências (1) */}
            <div className="flex flex-wrap items-stretch gap-4">
              {temDados ? (
                <section className="flex min-w-0 flex-[2_1_560px] flex-col rounded-xl border bg-card">
                  <div className="flex flex-col gap-[18px] px-6 pb-5 pt-6">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex flex-col gap-1.5">
                        <span className="text-sm text-muted-foreground">Custo total da empresa</span>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-xl font-medium text-muted-foreground">R$</span>
                          <span className="text-[44px] font-semibold leading-none tracking-heroi">{inteiro}</span>
                          <span className="text-xl font-medium text-muted-foreground">,{centavos}</span>
                        </div>
                      </div>
                      <span className="inline-flex h-7 items-center rounded-[7px] bg-muted px-2.5 text-[13px] font-medium">
                        {textoVariacao(total.custo, anterior.custo, competencia)}
                      </span>
                    </div>

                    <div className="flex flex-col gap-3">
                      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full" aria-hidden>
                        {partes.map((p) => (
                          <div
                            key={p.chave}
                            title={p.rotulo}
                            style={{ width: `${(p.valor / somaPartes) * 100}%`, background: p.cor }}
                          />
                        ))}
                      </div>
                      <div
                        className="grid gap-x-5 gap-y-3"
                        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}
                      >
                        {partes.map((p) => (
                          <div key={p.chave} className="flex items-start gap-2">
                            <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: p.cor }} />
                            <div className="flex min-w-0 flex-col gap-0.5">
                              <span className="text-xs text-muted-foreground">{p.rotulo}</span>
                              <span className="text-[15px] font-semibold">{formatCents(p.valor)}</span>
                              <span className="text-xs text-muted-foreground">
                                {porcento(p.valor / somaPartes)}
                                {p.apoio && ` · ${p.apoio}`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <FaixaKpis
                    embutida
                    className="mt-auto"
                    itens={[
                      {
                        rotulo: "Pessoas na folha",
                        valor: String(total.headcount.total),
                        apoio: `${total.headcount.pj} PJ · ${total.headcount.clt} CLT`,
                      },
                      {
                        rotulo: "Custo médio por pessoa",
                        valor: formatCents(total.custoMedio),
                        apoio: textoVariacao(total.custoMedio, anterior.custoMedio, competencia),
                      },
                      {
                        rotulo: "Líquido pago na folha",
                        valor: reaisCompacto(total.liquido),
                        apoio: "NFs dos PJ + líquido dos holerites",
                      },
                      {
                        rotulo: "Empréstimos e descontos",
                        valor: reaisCompacto(total.emprestimos),
                        apoio: `abatidos · saldo em aberto ${reaisCompacto(op.saldoEmprestimos)}`,
                      },
                    ]}
                  />
                </section>
              ) : (
                <section className="flex min-w-0 flex-[2_1_560px] items-center justify-center rounded-xl border bg-card px-6 py-16 text-center text-sm text-muted-foreground">
                  Nada lançado em {formatMesRef(competencia)} ainda: abra a folha, a folha de benefícios ou a{" "}
                  {nomeBonificacao.toLowerCase()} do mês.
                </section>
              )}

              <section className="flex min-w-0 flex-[1_1_320px] flex-col rounded-xl border bg-card">
                <div className="flex items-center gap-2.5 px-5 pb-3 pt-5">
                  <span className="flex h-[30px] w-[30px] items-center justify-center rounded-lg bg-amber-500/15 text-amber-700">
                    <AlertTriangle className="h-4 w-4" />
                  </span>
                  <h3 className="flex-1 text-base font-semibold">Precisa de ação</h3>
                  {pendencias.length > 0 && (
                    <span className="text-xs font-semibold text-muted-foreground">
                      {pendencias.length} {pendencias.length === 1 ? "item" : "itens"}
                    </span>
                  )}
                </div>
                {pendencias.length > 0 ? (
                  <div className="flex flex-col px-2 pb-2">
                    {pendencias.map((p) => (
                      <Link
                        key={p.texto}
                        to={p.link}
                        className="grid grid-cols-[44px_1fr_16px] items-center gap-2 rounded-lg border-t border-border-soft px-3 py-2.5 transition-colors hover:bg-muted-soft"
                      >
                        <span className={cn("text-lg font-semibold", COR_TOM[p.tom])}>{p.n}</span>
                        <span className="flex min-w-0 flex-col gap-px">
                          <span className="text-[13px] font-medium leading-snug">{p.texto}</span>
                          <span className="text-xs text-muted-foreground">{p.onde}</span>
                        </span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-1 flex-col items-center justify-center gap-2 px-5 py-8 text-center text-sm text-muted-foreground">
                    <CheckCircle2 className="h-7 w-7 text-success" />
                    Nenhuma pendência nesta competência.
                  </div>
                )}
              </section>
            </div>

            {/* Evolução de 12 meses: clicar numa coluna troca a competência */}
            <SecaoCard
              titulo="Evolução do custo — últimos 12 meses"
              descricao="Clique numa coluna para abrir aquela competência."
              acoes={
                <>
                  <SegmentedControl<ModoGrafico>
                    aria-label="Visão do gráfico"
                    value={modoGrafico}
                    onChange={setModoGrafico}
                    options={[
                      { value: "composicao", label: "Composição" },
                      { value: "pessoa", label: "Por pessoa" },
                    ]}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-[34px] text-xs"
                    onClick={() => setMostrarTabela((t) => !t)}
                    aria-pressed={mostrarTabela}
                  >
                    {mostrarTabela ? <BarChart3 className="!size-3.5" /> : <Table2 className="!size-3.5" />}
                    {mostrarTabela ? "Ver gráfico" : "Ver tabela"}
                  </Button>
                </>
              }
            >
              <div className="flex flex-col gap-4">
                <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground" aria-label="Legenda">
                  {series.map((s) => (
                    <li key={s.chave} className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: s.cor }} aria-hidden />
                      {s.rotulo}
                    </li>
                  ))}
                  {media != null && (
                    <li className="flex items-center gap-1.5">
                      <span className="w-4 border-t-[1.5px] border-dashed border-muted-foreground" aria-hidden />
                      Média 12 meses: {formatarMedia(media)}
                    </li>
                  )}
                </ul>

                {mostrarTabela ? (
                  <div className="max-h-[340px] overflow-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Competência</TableHead>
                          {porPessoa ? (
                            <>
                              <TableHead className="text-right">Pessoas</TableHead>
                              <TableHead className="text-right">Custo por pessoa</TableHead>
                            </>
                          ) : (
                            <>
                              {series.map((s) => (
                                <TableHead key={s.chave} className="text-right">
                                  {s.rotulo}
                                </TableHead>
                              ))}
                              <TableHead className="text-right">Total</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[...evolucao].reverse().map((e) => (
                          <TableRow
                            key={e.competencia}
                            className={cn("cursor-pointer", e.competencia === competencia && "bg-primary/[0.06]")}
                            onClick={() => e.competencia !== competencia && void load(e.competencia)}
                          >
                            <TableCell>{mesCurto(e.competencia)}</TableCell>
                            {porPessoa ? (
                              <>
                                <TableCell className="text-right">{e.headcount}</TableCell>
                                <TableCell className="text-right font-semibold">
                                  {formatCents(custoPorPessoa(e))}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell className="text-right">{formatCents(e.folhaPj)}</TableCell>
                                <TableCell className="text-right">{formatCents(e.folhaClt)}</TableCell>
                                <TableCell className="text-right">{formatCents(e.beneficios)}</TableCell>
                                <TableCell className="text-right">{formatCents(e.bonificacao)}</TableCell>
                                <TableCell className="text-right font-semibold">{formatCents(e.custo)}</TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <GraficoEvolucao
                    colunas={evolucao.map((e) => ({
                      chave: e.competencia,
                      rotulo: mesCurto(e.competencia),
                      valores: porPessoa
                        ? { porPessoa: custoPorPessoa(e) }
                        : {
                            folhaPj: e.folhaPj,
                            folhaClt: e.folhaClt,
                            beneficios: e.beneficios,
                            bonificacao: e.bonificacao,
                          },
                    }))}
                    series={series}
                    selecionada={competencia}
                    onSelecionar={(c) => c !== competencia && void load(c)}
                    media={media}
                    formatarValor={formatCents}
                    formatarEixo={reaisCompacto}
                    formatarTotal={porPessoa ? (v) => formatCents(v).replace(/,\d{2}$/, "") : reaisCompacto}
                  />
                )}
              </div>
            </SecaoCard>

            {temDados && (
              <CardOndeEstaOCusto
                dados={dados}
                nomeBonificacao={nomeBonificacao}
                quebra={quebra}
                onQuebra={setQuebra}
                detalhar={detalharColunas}
                onDetalhar={setDetalharColunas}
                abertos={tomadoresAbertos}
                onAlternar={alternarTomador}
              />
            )}
          </div>
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHDashboard;
